import { memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MIN_CARD_HEIGHT_PX } from "@miplatform/constants";
import { useDroppable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import { IPrioritySchedulerColumnProps } from "../PrioritySchedulerBoard.types";
import { getTemplateWindowsByIdForCalendar } from "../PrioritySchedulerBoard.utils";
import { useEventLayouts } from "../useEventLayouts";
import { DisabledSlots } from "./DisabledSlots";
import { DraggableEventCard } from "./DraggableEventCard";
import { DraggingLineIndicator } from "./DraggingLineIndicator";

const STACK_GAP_PX = 1;

function PrioritySchedulerColumnComponent({
  EventComponent,
  id,
  columnDnDId,
  events = [],
  holiday,
  hourHeight,
  disabledSlots = [],
  dayStartTime,
  dayEndTime,
  date: columnDate,
  layoutReferenceDate,
  timelineContentHeight,
  dragIndicatorY,
  workTemplateBases,
  highlightedEventsMap,
  onEventRemove,
  onCardClick,
  registerColumnDroppableNode,
  dateView,
}: Readonly<IPrioritySchedulerColumnProps>) {
  const [hours, minutes] = dayStartTime?.split(":").map(Number) ?? [0, 0];
  const hoursToDayStartTime = hours + (minutes ?? 0) / 60;
  const topPosition = hourHeight ? hoursToDayStartTime * hourHeight : 0;

  const [endHours, endMinutes] = dayEndTime?.split(":").map(Number) ?? [0, 0];
  const endHour = endHours + (endMinutes ?? 0) / 60;
  const endTopPosition = hourHeight ? endHour * hourHeight : 0;

  const templateWindowsById = useMemo(
    () => getTemplateWindowsByIdForCalendar(workTemplateBases),
    [workTemplateBases]
  );

  const { layouts, maxBottom } = useEventLayouts({
    events,
    hourHeight,
    columnDate: columnDate ?? layoutReferenceDate,
    dayStartTime,
    dayEndTime,
    stackGapPx: STACK_GAP_PX,
    templateWindowsById,
    disabledSlots,
  });

  const schedulableHeight = Math.max(
    timelineContentHeight - topPosition,
    maxBottom + STACK_GAP_PX * 2
  );

  const { classes } = useStyles({
    isHoliday: !!holiday,
    topPosition,
    endTopPosition,
    schedulableHeight,
  });
  const droppableId = columnDnDId ?? `column:${id}`;
  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id: droppableId,
    disabled: Boolean(dateView),
    data: {
      type: "container",
      container: { type: "column", id },
    },
  });

  const setDropContainerRef = (node: HTMLDivElement | null) => {
    setDroppableNodeRef(node);
    registerColumnDroppableNode?.(id, node);
  };

  const sortedForPaint = useMemo(() => {
    return [...events].sort((a, b) => {
      const ta = layouts.get(a.id)?.top ?? 0;
      const tb = layouts.get(b.id)?.top ?? 0;
      return ta - tb;
    });
  }, [events, layouts]);

  return (
    <Box className={classes.columnContainer}>
      <DisabledSlots
        disabledSlots={disabledSlots}
        holiday={holiday}
        hourHeight={hourHeight}
      />

      {dayEndTime && !holiday && (
        <Box className={classes.afterDayEndContainer} />
      )}

      <Box ref={setDropContainerRef} className={classes.cardContainer}>
        {dragIndicatorY != null && (
          <DraggingLineIndicator offsetY={dragIndicatorY} />
        )}
        {sortedForPaint.map((event) => {
          const layout = layouts.get(event.id) ?? {
            top: 0,
            height: hourHeight,
          };

          const isMinimized = layout.top >= endTopPosition - topPosition;
          const height = isMinimized ? MIN_CARD_HEIGHT_PX : layout.height;

          return (
            <DraggableEventCard
              key={event.id}
              event={event}
              layout={{ top: layout.top, height }}
              columnId={id}
              isHighlighted={highlightedEventsMap?.[event.id]}
              onEventRemove={onEventRemove}
              onCardClick={onCardClick}
              EventComponent={EventComponent}
              isMinimized={isMinimized}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export const PrioritySchedulerColumn = memo(PrioritySchedulerColumnComponent);

const useStyles = makeStyles<{
  isHoliday?: boolean;
  topPosition: number;
  endTopPosition: number;
  schedulableHeight: number;
}>()(
  (theme, { isHoliday, topPosition, endTopPosition, schedulableHeight }) => ({
    columnContainer: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      backgroundColor: isHoliday
        ? theme.palette.grey.lightAccent
        : theme.palette.info.light,
      padding: theme.spacing(0, 1),
      height: "100%",
      width: "100%",
      minHeight: 0,
      flex: 1,
      borderBottomRightRadius: theme.spacing(1),
      borderBottomLeftRadius: theme.spacing(1),
    },
    afterDayEndContainer: {
      position: "absolute",
      top: endTopPosition,
      left: 0,
      right: 0,
      bottom: -38, // sticky header height
      backgroundColor: theme.palette.grey.accent,
      pointerEvents: "none",
      borderBottomRightRadius: theme.spacing(1),
      borderBottomLeftRadius: theme.spacing(1),
      height: "40%", // half of the height of the column for pushed cards
    },
    cardContainer: {
      position: "absolute",
      top: topPosition,
      left: 0,
      right: 0,
      width: "100%",
      padding: theme.spacing(0, 1),
      minHeight: schedulableHeight,
    },
    dropIndicatorLine: {
      position: "absolute",
      left: theme.spacing(1),
      right: theme.spacing(1),
      height: 2,
      backgroundColor: theme.palette.primary.main,
      borderRadius: theme.spacing(0.5),
      pointerEvents: "none",
    },
    ghostEvent: {
      opacity: 0.5,
    },
    eventContainer: {
      width: "100%",
    },
  })
);
